import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiFilter, FiX } from 'react-icons/fi';
import { FaComment } from 'react-icons/fa';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRefresh, faEye, faChartBar } from '@fortawesome/free-solid-svg-icons';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, ICellRendererParams, ModuleRegistry, AllCommunityModule } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-theme-material.css';
import '../styles/ag-grid-custom.css';
import apiService from '../services/apiService';

// Register AG Grid modules
ModuleRegistry.registerModules([AllCommunityModule]);

interface FeedbackStats {
  total: number;
  byStatus: Record<string, number>;
  byType: Record<string, number>;
}

const FeedbackList: React.FC = () => {
  const navigate = useNavigate();
  const [feedbacks, setFeedbacks] = useState<any[]>([]);
  const [stats, setStats] = useState<FeedbackStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingStats, setLoadingStats] = useState(false);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [showStats, setShowStats] = useState(false);

  useEffect(() => {
    fetchFeedbacks();
    fetchStats();
  }, []);

  useEffect(() => {
    fetchFeedbacks();
  }, [statusFilter, typeFilter]);

  const fetchFeedbacks = async () => {
    try {
      setLoading(true);
      const filters: any = {};
      if (statusFilter) filters.status = statusFilter;
      if (typeFilter) filters.type = typeFilter;

      const data = await apiService.getFeedbacks(filters);
      // Handle both array and paginated response
      const feedbackArray = Array.isArray(data) ? data : data.feedbacks || data.data || [];
      setFeedbacks(feedbackArray);
      setError('');
    } catch (err: any) {
      setError(err.message || 'Failed to load feedbacks');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      setLoadingStats(true);
      const data = await apiService.getFeedbackStats();
      setStats(data);
    } catch (err: any) {
      console.error('Failed to load stats:', err);
    } finally {
      setLoadingStats(false);
    }
  };

  const getStatusColor = (status: string) => {
    const statusLower = (status || '').toLowerCase();
    if (statusLower === 'new' || statusLower === 'pending') return 'bg-blue-100 text-blue-800 border-blue-300';
    if (statusLower === 'in_progress' || statusLower === 'in-progress' || statusLower === 'reviewing') return 'bg-yellow-100 text-yellow-800 border-yellow-300';
    if (statusLower === 'resolved' || statusLower === 'completed' || statusLower === 'closed') return 'bg-green-100 text-green-800 border-green-300';
    if (statusLower === 'rejected' || statusLower === 'dismissed') return 'bg-red-100 text-red-800 border-red-300';
    return 'bg-gray-100 text-gray-600 border-gray-200';
  };

  const getTypeColor = (type: string) => {
    const typeLower = (type || '').toLowerCase();
    if (typeLower === 'bug' || typeLower === 'issue') return 'bg-red-100 text-red-800';
    if (typeLower === 'feature' || typeLower === 'suggestion') return 'bg-purple-100 text-purple-800';
    if (typeLower === 'improvement') return 'bg-blue-100 text-blue-800';
    if (typeLower === 'question') return 'bg-cyan-100 text-cyan-800';
    return 'bg-gray-100 text-gray-600';
  };

  // Generate dynamic columns from first item
  const columnDefs: ColDef[] = useMemo(() => {
    if (feedbacks.length === 0) return [];

    const firstItem = feedbacks[0];
    const keys = Object.keys(firstItem);

    // Fields to exclude from grid
    const excludedFields = ['__v', 'userInfo', 'user_info', 'metadata', 'attachments'];
    const idFieldsToExclude = ['userId', 'user_id'];

    // Filter and map columns
    const dataColumns = keys
      .filter((key) => {
        const keyLower = key.toLowerCase();
        const value = firstItem[key];
        if (excludedFields.some(excluded => keyLower === excluded.toLowerCase())) return false;
        if (idFieldsToExclude.some(excluded => keyLower === excluded.toLowerCase())) return false;
        if (value !== null && typeof value === 'object' && !Array.isArray(value)) return false;
        if (Array.isArray(value)) return false;
        return true;
      })
      .map((key) => {
        const value = firstItem[key];
        const isDate = key.toLowerCase().includes('date') || key.toLowerCase().includes('time') || key.toLowerCase().includes('createdat') || key.toLowerCase().includes('updatedat');
        const isBoolean = typeof value === 'boolean';

        // Format header name
        const headerName = key
          .replace(/([A-Z])/g, ' $1')
          .replace(/_/g, ' ')
          .replace(/^\w/, (c) => c.toUpperCase())
          .trim();

        const columnDef: ColDef = {
          field: key,
          headerName: headerName,
          filter: isDate ? 'agDateColumnFilter' : 'agTextColumnFilter',
          sortable: true,
          resizable: true,
          minWidth: 150,
          flex: 1,
        };

        // Custom rendering for specific fields
        if (isBoolean) {
          columnDef.minWidth = 100;
          columnDef.flex = 0.5;
          columnDef.cellRenderer = (params: ICellRendererParams) => (
            <span className={`inline-flex px-2 py-1 rounded-full text-xs font-semibold ${
              params.value ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
            }`}>
              {params.value ? 'Yes' : 'No'}
            </span>
          );
        } else if (key.toLowerCase() === 'status') {
          columnDef.minWidth = 140;
          columnDef.flex = 0.8;
          columnDef.cellRenderer = (params: ICellRendererParams) => {
            const statusColor = getStatusColor(params.value || '');
            return (
              <span className={`inline-flex px-2 py-1 rounded-full text-xs font-semibold border ${statusColor}`}>
                {params.value || 'N/A'}
              </span>
            );
          };
        } else if (key.toLowerCase() === 'type' || key.toLowerCase() === 'category') {
          columnDef.minWidth = 120;
          columnDef.flex = 0.7;
          columnDef.cellRenderer = (params: ICellRendererParams) => {
            const typeColor = getTypeColor(params.value || '');
            return (
              <span className={`inline-flex px-2 py-1 rounded-full text-xs font-semibold ${typeColor}`}>
                {params.value || 'N/A'}
              </span>
            );
          };
        } else if (key.toLowerCase().includes('message') || key.toLowerCase().includes('description') || key.toLowerCase().includes('content')) {
          columnDef.minWidth = 300;
          columnDef.flex = 2;
          columnDef.cellRenderer = (params: ICellRendererParams) => (
            <div className="truncate max-w-md" title={params.value || ''}>
              {params.value || 'N/A'}
            </div>
          );
        } else if (isDate) {
          columnDef.minWidth = 180;
          columnDef.flex = 1;
          columnDef.valueFormatter = (params) => {
            if (!params.value) return 'N/A';
            try {
              return new Date(params.value).toLocaleString();
            } catch {
              return params.value;
            }
          };
        } else if (key.toLowerCase().includes('id')) {
          columnDef.minWidth = 220;
          columnDef.flex = 1.2;
          columnDef.cellRenderer = (params: ICellRendererParams) => (
            <div className="font-mono text-xs">{params.value || 'N/A'}</div>
          );
        } else if (key.toLowerCase() === 'email') {
          columnDef.minWidth = 200;
          columnDef.flex = 1.2;
        }

        return columnDef;
      });

    // Add Actions column
    const actionsColumn: ColDef = {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      pinned: 'right',
      sortable: false,
      filter: false,
      resizable: false,
      suppressSizeToFit: true,
      cellRenderer: (params: ICellRendererParams) => {
        const feedback = params.data;
        const feedbackId = feedback._id || feedback.id;

        return (
          <div className="flex gap-2">
            <button
              onClick={() => navigate(`/dashboard/feedback/${feedbackId}`, { state: { feedback } })}
              className="inline-flex items-center px-3 py-1 bg-indigo-50 text-indigo-700 rounded-md hover:bg-indigo-100 transition-colors text-xs font-medium"
            >
              <FontAwesomeIcon icon={faEye} className="mr-1" />
              View
            </button>
          </div>
        );
      },
    };

    return [...dataColumns, actionsColumn];
  }, [feedbacks, navigate]);

  const defaultColDef = useMemo(() => ({
    sortable: true,
    resizable: true,
    filter: true,
    minWidth: 120,
    wrapText: true,
    autoHeight: false,
    valueFormatter: (params: any) => {
      if (params.value === null || params.value === undefined) return '';
      if (typeof params.value === 'object') {
        if (Array.isArray(params.value)) {
          return `[${params.value.length} items]`;
        }
        const keys = Object.keys(params.value);
        if (keys.length === 0) return '{}';
        if (params.value.name) return params.value.name;
        if (params.value.title) return params.value.title;
        return `{${keys.length} fields}`;
      }
      return params.value;
    },
  }), []);

  const onGridReady = (params: any) => {
    params.api.autoSizeAllColumns(false);
    setTimeout(() => {
      const allColumns = params.api.getColumns();
      if (allColumns) {
        const widthConfig: any = {};
        allColumns.forEach((column: any) => {
          const currentWidth = column.getActualWidth();
          const colId = column.getColId();
          widthConfig[colId] = currentWidth + 20;
        });
        params.api.setColumnWidths(Object.entries(widthConfig).map(([key, width]) => ({
          key,
          newWidth: width as number
        })));
      }
    }, 100);
  };

  const clearFilters = () => {
    setStatusFilter('');
    setTypeFilter('');
  };

  // Get unique status and type values for filters
  const statusOptions = useMemo(() => {
    if (stats?.byStatus) {
      return Object.keys(stats.byStatus);
    }
    const statuses = new Set(feedbacks.map(f => f.status).filter(Boolean));
    return Array.from(statuses);
  }, [feedbacks, stats]);

  const typeOptions = useMemo(() => {
    if (stats?.byType) {
      return Object.keys(stats.byType);
    }
    const types = new Set(feedbacks.map(f => f.type || f.category).filter(Boolean));
    return Array.from(types);
  }, [feedbacks, stats]);

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Feedback</h1>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            {feedbacks.length} feedback{feedbacks.length !== 1 ? 's' : ''} found
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowStats(!showStats)}
            className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-dark-300/40 rounded-lg shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-dark-400/60 hover:bg-gray-50 dark:hover:bg-dark-400/80 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <FontAwesomeIcon icon={faChartBar} className="mr-2" />
            {showStats ? 'Hide Stats' : 'Show Stats'}
          </button>
          <button
            onClick={fetchFeedbacks}
            disabled={loading}
            className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-dark-300/40 rounded-lg shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-dark-400/60 hover:bg-gray-50 dark:hover:bg-dark-400/80 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            <FontAwesomeIcon icon={faRefresh} className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      {showStats && stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white dark:bg-dark-400/60 rounded-lg border border-gray-200 dark:border-dark-300/40 p-4">
            <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Feedback</div>
            <div className="mt-1 text-3xl font-bold text-gray-900 dark:text-white">{stats.total || feedbacks.length}</div>
          </div>
          {stats.byStatus && Object.entries(stats.byStatus).map(([status, count]) => (
            <div key={status} className="bg-white dark:bg-dark-400/60 rounded-lg border border-gray-200 dark:border-dark-300/40 p-4">
              <div className="text-sm font-medium text-gray-500 dark:text-gray-400 capitalize">{status.replace(/_/g, ' ')}</div>
              <div className="mt-1 text-3xl font-bold text-gray-900 dark:text-white">{count}</div>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white dark:bg-dark-400/60 backdrop-blur-sm rounded-lg dark:rounded-xl border border-gray-200 dark:border-dark-300/40 shadow-sm dark:shadow-lg p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center">
            <FiFilter className="h-4 w-4 mr-2 text-gray-500" />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Filters:</span>
          </div>

          <div className="flex items-center gap-2">
            <label htmlFor="status-filter" className="text-sm text-gray-600 dark:text-gray-400">Status:</label>
            <select
              id="status-filter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-1.5 border border-gray-300 dark:border-dark-300/40 bg-white dark:bg-dark-400/60 text-gray-900 dark:text-white rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All</option>
              {statusOptions.map((status) => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-2">
            <label htmlFor="type-filter" className="text-sm text-gray-600 dark:text-gray-400">Type:</label>
            <select
              id="type-filter"
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="px-3 py-1.5 border border-gray-300 dark:border-dark-300/40 bg-white dark:bg-dark-400/60 text-gray-900 dark:text-white rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All</option>
              {typeOptions.map((type) => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>

          {(statusFilter || typeFilter) && (
            <button
              onClick={clearFilters}
              className="inline-flex items-center px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
            >
              <FiX className="h-4 w-4 mr-1" />
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 dark:bg-danger-500/20 border border-red-200 dark:border-danger-500/30 rounded-lg p-4">
          <div className="text-sm text-red-800 dark:text-danger-500">{error}</div>
        </div>
      )}

      {/* AG Grid */}
      {feedbacks.length > 0 && (
        <div className="bg-white dark:bg-dark-400/60 backdrop-blur-sm rounded-lg dark:rounded-xl border border-gray-200 dark:border-dark-300/40 shadow-sm dark:shadow-lg overflow-hidden">
          <div className="ag-theme-material" style={{ height: 'calc(100vh - 400px)', minHeight: '400px', width: '100%' }}>
            <AgGridReact
              rowData={feedbacks}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              pagination={true}
              paginationPageSize={25}
              paginationPageSizeSelector={[10, 25, 50, 100]}
              loading={loading}
              rowHeight={60}
              animateRows={true}
              enableCellTextSelection={true}
              onGridReady={onGridReady}
              onFirstDataRendered={(params) => params.api.autoSizeAllColumns()}
            />
          </div>
        </div>
      )}

      {/* Empty State */}
      {!loading && feedbacks.length === 0 && (
        <div className="bg-white dark:bg-dark-400/60 backdrop-blur-sm rounded-lg dark:rounded-xl border border-gray-200 dark:border-dark-300/40 shadow-sm dark:shadow-lg">
          <div className="text-center py-12">
            <div className="mx-auto h-16 w-16 bg-gray-100 dark:bg-dark-300/40 rounded-full flex items-center justify-center mb-4">
              <FaComment className="h-8 w-8 text-gray-400 dark:text-gray-500" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No feedback found</h3>
            <p className="text-gray-600 dark:text-gray-400">
              {statusFilter || typeFilter
                ? 'Try adjusting your filters'
                : 'No feedback has been submitted yet'}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default FeedbackList;
